import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  DollarSign,
  UserX,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  Edit2,
  X,
  Mail,
  Phone,
  MapPin,
  FileText,
  CreditCard,
  Hash,
  Plus,
  Megaphone,
  Headset,
  PhoneCall,
} from "lucide-react";
import { useAuth, ProtectedRoute } from "@/lib/auth-context";
import { GlassCard } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { FloAgentBar } from "@/components/FloAgent";
import {
  getDashboardStats,
  getCalendar,
  getDayAppointments,
  getAppointment,
  updateAppointmentStatus,
  createAppointment,
  type DashboardStats,
  type CalendarData,
  type Appointment,
  type AppointmentDetail,
  type AppointmentStatus,
  type CreateAppointmentData,
} from "@/lib/appointments-api";
import { getOnboardingStatus } from "@/lib/settings-api";
import { getClients, createClient, type Client } from "@/lib/clients-api";
import { getServices, type Service } from "@/lib/services-api";
import { getProviders, getProviderAvailability, type Provider, type TimeSlot } from "@/lib/providers-api";
import { getLocations, type Location } from "@/lib/locations-api";

// ════════════════════════════════════════════════════════════════════════════
// Dashboard Page
// ════════════════════════════════════════════════════════════════════════════

function DashboardContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [stats, setStats] = useState<DashboardStats | null>(null);

  // Check if onboarding is complete - redirect to onboarding if not
  useEffect(() => {
    async function checkOnboarding() {
      // Skip for platform admins
      if (user?.isPlatformUser) return;

      try {
        const status = await getOnboardingStatus();
        if (status.status === "setup") {
          navigate("/onboarding");
        }
      } catch (err) {
        // If we can't check onboarding status, don't block the dashboard
        console.error("Failed to check onboarding status:", err);
      }
    }
    checkOnboarding();
  }, [user, navigate]);
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const parts = new Date().toISOString().split("T");
    return parts[0] ?? new Date().toISOString().slice(0, 10);
  });
  const [dayAppointments, setDayAppointments] = useState<Appointment[]>([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Load appointment details when clicked
  async function handleAppointmentClick(apptId: string) {
    setDetailLoading(true);
    try {
      const detail = await getAppointment(apptId);
      setSelectedAppointment(detail);
    } catch (err) {
      toast.error("Failed to load appointment details");
    } finally {
      setDetailLoading(false);
    }
  }

  // Load dashboard stats
  useEffect(() => {
    async function loadStats() {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (err) {
        console.error("Failed to load stats:", err);
      }
    }
    loadStats();
  }, []);

  // Load calendar data when month changes
  useEffect(() => {
    async function loadCalendar() {
      setLoading(true);
      try {
        const data = await getCalendar({ month: currentMonth });
        setCalendarData(data);
      } catch (err) {
        console.error("Failed to load calendar:", err);
      } finally {
        setLoading(false);
      }
    }
    loadCalendar();
  }, [currentMonth]);

  // Load day appointments when selected date changes
  useEffect(() => {
    async function loadDay() {
      setDayLoading(true);
      try {
        const data = await getDayAppointments({ date: selectedDate });
        setDayAppointments(data.appointments);
      } catch (err) {
        console.error("Failed to load day:", err);
      } finally {
        setDayLoading(false);
      }
    }
    loadDay();
  }, [selectedDate]);

  // Handle new appointment creation
  async function handleAppointmentCreated() {
    // Refresh day appointments
    const dayData = await getDayAppointments({ date: selectedDate });
    setDayAppointments(dayData.appointments);
    // Refresh stats
    const newStats = await getDashboardStats();
    setStats(newStats);
    // Refresh calendar data
    const newCalendarData = await getCalendar({ month: currentMonth });
    setCalendarData(newCalendarData);
    setShowAddModal(false);
  }

  // Handle status change
  async function handleStatusChange(apptId: string, newStatus: AppointmentStatus) {
    try {
      await updateAppointmentStatus(apptId, newStatus);
      toast.success(`Appointment ${newStatus.replace("_", " ")}`);
      // Refresh day appointments
      const data = await getDayAppointments({ date: selectedDate });
      setDayAppointments(data.appointments);
      // Refresh stats
      const newStats = await getDashboardStats();
      setStats(newStats);
      // Refresh calendar data to update the monthly summary
      const newCalendarData = await getCalendar({ month: currentMonth });
      setCalendarData(newCalendarData);
    } catch (err) {
      toast.error("Failed to update appointment status");
    }
  }

  // Navigate months
  const goToPrevMonth = () => {
    const parts = currentMonth.split("-").map(Number);
    const year = parts[0] ?? 2024;
    const month = parts[1] ?? 1;
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    setCurrentMonth(`${prevYear}-${String(prevMonth).padStart(2, "0")}`);
  };

  const goToNextMonth = () => {
    const parts = currentMonth.split("-").map(Number);
    const year = parts[0] ?? 2024;
    const month = parts[1] ?? 1;
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    setCurrentMonth(`${nextYear}-${String(nextMonth).padStart(2, "0")}`);
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    const todayParts = now.toISOString().split("T");
    setSelectedDate(todayParts[0] ?? now.toISOString().slice(0, 10));
  };

  // Format month for display
  const monthDisplay = useMemo(() => {
    const parts = currentMonth.split("-").map(Number);
    const year = parts[0] ?? 2024;
    const month = parts[1] ?? 1;
    return new Date(year, month - 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }, [currentMonth]);

  return (
    <>
      <main className="p-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-af-900 dark:text-af-100">
              Welcome back{user?.name ? `, ${user.name}` : ""}
            </h1>
            <p className="text-af-700 dark:text-af-300 mt-1">
              Your appointment management dashboard is ready.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Today's Appointments"
              value={stats?.today.total?.toString() ?? "--"}
              subtext={stats ? `${stats.today.completed} completed` : undefined}
              icon={Calendar}
              delay={0}
            />
            <StatCard
              title="Week Revenue"
              value={stats?.weekRevenue ? `$${Number(stats.weekRevenue).toLocaleString()}` : "--"}
              subtext="This week"
              icon={DollarSign}
              delay={0.1}
            />
            <StatCard
              title="No-Show Rate"
              value={stats?.noShowRate !== undefined ? `${stats.noShowRate}%` : "--"}
              subtext="Last 30 days"
              icon={UserX}
              delay={0.2}
            />
            <StatCard
              title="New Clients"
              value={stats?.newClients?.toString() ?? "--"}
              subtext="This week"
              icon={UserPlus}
              delay={0.3}
            />
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendar - Takes 2 columns */}
            <GlassCard animated className="lg:col-span-2 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-af-900 dark:text-af-100">{monthDisplay}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 text-sm text-af-600 dark:text-af-400 hover:text-af-900 dark:hover:text-af-100 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={goToPrevMonth}
                    className="p-1 rounded hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                  >
                    <ChevronLeft size={20} className="text-af-600 dark:text-af-400" />
                  </button>
                  <button
                    onClick={goToNextMonth}
                    className="p-1 rounded hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                  >
                    <ChevronRight size={20} className="text-af-600 dark:text-af-400" />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="animate-spin h-8 w-8 border-2 border-af-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <MonthCalendar
                  month={currentMonth}
                  days={calendarData?.days ?? []}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              )}
            </GlassCard>

            {/* Today's Schedule - Takes 1 column, stretches to match calendar */}
            <GlassCard animated className="p-6 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-af-900 dark:text-af-100">
                  {formatDateHeader(selectedDate)}
                </h3>
                <div className="flex items-center gap-2">
                  {dayAppointments.length > 0 && (
                    <span className="text-sm text-af-500">
                      {dayAppointments.length} appointment{dayAppointments.length !== 1 ? "s" : ""}
                    </span>
                  )}
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="p-1.5 rounded-lg bg-af-700 hover:bg-af-800 text-white transition-colors"
                    title="Add Appointment"
                  >
                    <Plus size={16} className="inline-block" />
                    <span className="sr-only">Add Appointment</span>
                  </button>
                </div>
              </div>

              <div className="space-y-2 overflow-y-auto flex-1">
                {dayLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-2 border-af-500 border-t-transparent rounded-full" />
                  </div>
                ) : dayAppointments.length === 0 ? (
                  <div className="text-center py-8 text-af-500">
                    <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No appointments</p>
                  </div>
                ) : (
                  dayAppointments.map((appt) => (
                    <AppointmentCard
                      key={appt.id}
                      appointment={appt}
                      onStatusChange={handleStatusChange}
                      onClick={() => handleAppointmentClick(appt.id)}
                    />
                  ))
                )}
              </div>
            </GlassCard>
          </div>

          {/* Month Summary */}
          {calendarData && (
            <GlassCard animated className="mt-4 p-6">
              <h3 className="text-af-900 dark:text-af-100 mb-4">
                {monthDisplay} Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <SummaryItem label="Total" value={calendarData.totals.total} />
                <SummaryItem label="Scheduled" value={calendarData.totals.scheduled} color="blue" />
                <SummaryItem label="Completed" value={calendarData.totals.completed} color="green" />
                <SummaryItem label="No-Shows" value={calendarData.totals.noShow} color="red" />
                <SummaryItem label="Cancelled" value={calendarData.totals.cancelled} color="gray" />
                <SummaryItem
                  label="Revenue"
                  value={`$${Number(calendarData.totals.totalRevenue).toLocaleString()}`}
                  isText
                />
              </div>
            </GlassCard>
          )}
        </div>
      </main>

      {/* Appointment Detail Modal */}
      {(selectedAppointment || detailLoading) && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          loading={detailLoading}
          onClose={() => setSelectedAppointment(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Add Appointment Modal */}
      {showAddModal && (
        <AddAppointmentModal
          initialDate={selectedDate}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAppointmentCreated}
        />
      )}

      {/* Flo AI floating prompt bar */}
      <FloAgentBar />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Appointment Detail Modal Component
// ────────────────────────────────────────────────────────────────────────────

interface AppointmentDetailModalProps {
  appointment: AppointmentDetail | null;
  loading: boolean;
  onClose: () => void;
  onStatusChange: (id: string, status: AppointmentStatus) => Promise<void>;
}

// ────────────────────────────────────────────────────────────────────────────
// Front-desk (virtual receptionist) confirmation chip
// ────────────────────────────────────────────────────────────────────────────

const VR_CHIP_LABELS: Record<string, string> = {
  confirmed: "Confirmed by front desk",
  no_answer: "No answer",
  voicemail: "Voicemail left",
  reschedule_requested: "Wants to reschedule",
  bad_contact: "Bad phone number",
};

const VR_CHIP_STYLES: Record<string, string> = {
  confirmed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  no_answer: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  voicemail: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  reschedule_requested: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  bad_contact: "bg-red-500/15 text-red-600 dark:text-red-400",
};

function VrConfirmationChip({ status, attempts }: { status?: string; attempts?: number }) {
  if (!status || status === "unconfirmed") return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${VR_CHIP_STYLES[status] ?? "bg-af-500/15 text-af-700 dark:text-af-300"}`}
      title={attempts ? `${attempts} call attempt${attempts === 1 ? "" : "s"}` : undefined}
    >
      <PhoneCall size={10} />
      {VR_CHIP_LABELS[status] ?? status.replace(/_/g, " ")}
    </span>
  );
}

function AppointmentDetailModal({ appointment, loading, onClose, onStatusChange }: AppointmentDetailModalProps) {
  const statusColors: Record<AppointmentStatus, string> = {
    scheduled: "bg-blue-100 text-blue-800",
    checked_in: "bg-yellow-100 text-yellow-800",
    completed: "bg-green-100 text-green-800",
    no_show: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-800",
  };

  const formatTime = (time: string) => {
    const parts = time.split(":");
    const hours = parts[0] ?? "00";
    const minutes = parts[1] ?? "00";
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleStatusUpdate = async (newStatus: AppointmentStatus) => {
    if (!appointment) return;
    await onStatusChange(appointment.id, newStatus);
    onClose();
  };

  // Calculate deposit from transactions
  const depositAmount = appointment?.transactions
    ?.filter((t) => t.type === "deposit" && t.status === "completed")
    .reduce((sum, t) => sum + parseFloat(t.amount), 0) ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <GlassCard className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-af-900 dark:text-af-100">
                Appointment Details
              </h2>
              {appointment && (
                <p className="text-sm text-af-500 mt-1">
                  {formatDate(appointment.date)}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-af-100 dark:hover:bg-af-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-af-500" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-af-500 border-t-transparent rounded-full" />
            </div>
          ) : appointment ? (
            <div className="space-y-6">
              {/* Status & Time */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-af-500" />
                  <span className="text-lg font-medium text-af-900 dark:text-af-100">
                    {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[appointment.status]}`}>
                  {appointment.status.replace("_", " ")}
                </span>
              </div>

              {/* Service Info */}
              <div className="p-4 bg-af-50 dark:bg-af-800/50 rounded-xl">
                <h3 className="font-semibold text-af-900 dark:text-af-100 mb-2">
                  {appointment.service?.name ?? appointment.campaignName ?? "Campaign Appointment"}
                </h3>
                {appointment.campaignName && (
                  <p className="text-xs text-purple-400 mb-2">{appointment.campaignName}</p>
                )}
                {appointment.service?.description && (
                  <p className="text-sm text-af-600 dark:text-af-400 mb-2">
                    {appointment.service.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-af-500">
                    {appointment.service?.durationMinutes ?? "—"} minutes
                  </span>
                  <span className="font-medium text-af-900 dark:text-af-100">
                    {appointment.service?.price ? `$${parseFloat(appointment.service.price).toFixed(2)}` : "Free"}
                  </span>
                </div>
              </div>

              {/* Client Info */}
              <div>
                <h4 className="text-sm font-medium text-af-500 mb-3">Client Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-af-400" />
                    <span className="text-af-900 dark:text-af-100">{appointment.client.name}</span>
                  </div>
                  {appointment.client.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-af-400" />
                      <a href={`mailto:${appointment.client.email}`} className="text-af-600 dark:text-af-400 hover:underline">
                        {appointment.client.email}
                      </a>
                    </div>
                  )}
                  {appointment.client.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-af-400" />
                      <a href={`tel:${appointment.client.phone}`} className="text-af-600 dark:text-af-400 hover:underline">
                        {appointment.client.phone}
                      </a>
                    </div>
                  )}
                  {appointment.client.allergies && (
                    <div className="flex items-start gap-3 mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-red-700 dark:text-red-400">Allergies</p>
                        <p className="text-sm text-red-600 dark:text-red-300">{appointment.client.allergies}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Provider & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-af-500 mb-2">Provider</h4>
                  <p className="text-af-900 dark:text-af-100">{appointment.provider?.name ?? "—"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-af-500 mb-2">Location</h4>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-af-400 mt-0.5" />
                    <div>
                      <p className="text-af-900 dark:text-af-100">{appointment.location?.name ?? "—"}</p>
                      {appointment.location?.address && (
                        <p className="text-xs text-af-500">{appointment.location.address}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Code */}
              <div className="flex items-center gap-3 p-3 bg-af-100 dark:bg-af-800 rounded-lg">
                <Hash className="w-4 h-4 text-af-500" />
                <div>
                  <p className="text-xs text-af-500">Confirmation Code</p>
                  <p className="font-mono font-medium text-af-900 dark:text-af-100">
                    {appointment.confirmationCode}
                  </p>
                </div>
              </div>

              {/* Front Desk Follow-Up (virtual receptionist) */}
              {(appointment.vrConfirmationStatus !== "unconfirmed" ||
                (appointment.vrNotes && appointment.vrNotes.length > 0)) && (
                <div>
                  <h4 className="text-sm font-medium text-af-500 mb-2 flex items-center gap-2">
                    <Headset className="w-4 h-4" />
                    Front Desk Follow-Up
                  </h4>
                  <div className="p-3 bg-af-50 dark:bg-af-800/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <VrConfirmationChip
                        status={appointment.vrConfirmationStatus}
                        attempts={appointment.vrConfirmationAttempts}
                      />
                      {appointment.vrLastAttemptAt && (
                        <span className="text-xs text-af-500">
                          Last call {new Date(appointment.vrLastAttemptAt).toLocaleString()}
                          {appointment.vrConfirmationAttempts
                            ? ` · ${appointment.vrConfirmationAttempts} attempt${appointment.vrConfirmationAttempts === 1 ? "" : "s"}`
                            : ""}
                        </span>
                      )}
                    </div>
                    {appointment.vrNotes?.map((n) => (
                      <div key={n.id} className="pt-2 border-t border-af-200/50 dark:border-af-700/50">
                        <p className="text-sm text-af-700 dark:text-af-300 whitespace-pre-wrap">{n.body}</p>
                        <p className="text-xs text-af-500 mt-1">
                          {n.authorName ?? "Front desk"} · {new Date(n.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deposit Info */}
              {depositAmount > 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CreditCard className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 dark:text-green-400">Deposit Paid</p>
                    <p className="font-medium text-green-700 dark:text-green-300">
                      ${depositAmount.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Client Notes */}
              {appointment.clientNotes && (
                <div>
                  <h4 className="text-sm font-medium text-af-500 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Client Notes
                  </h4>
                  <p className="text-sm text-af-700 dark:text-af-300 p-3 bg-af-50 dark:bg-af-800/50 rounded-lg">
                    {appointment.clientNotes}
                  </p>
                </div>
              )}

              {/* Staff Notes */}
              {appointment.notes && (
                <div>
                  <h4 className="text-sm font-medium text-af-500 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Staff Notes
                  </h4>
                  <p className="text-sm text-af-700 dark:text-af-300 p-3 bg-af-50 dark:bg-af-800/50 rounded-lg">
                    {appointment.notes}
                  </p>
                </div>
              )}

              {/* Cancel Reason */}
              {appointment.cancelReason && (
                <div>
                  <h4 className="text-sm font-medium text-red-500 mb-2">Cancel Reason</h4>
                  <p className="text-sm text-red-600 dark:text-red-400 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    {appointment.cancelReason}
                  </p>
                </div>
              )}

              {/* Campaign Source */}
              {appointment.campaignLeadId && (
                <div className="flex items-center gap-2 mt-2">
                  <Megaphone size={14} className="text-purple-400 flex-shrink-0" />
                  <span className="text-sm text-af-500">
                    Source:{" "}
                    <span className="text-purple-400 font-medium">
                      {appointment.campaignName || "Campaign"}
                    </span>
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-4 border-t border-af-200 dark:border-af-700">
                {appointment.status === "scheduled" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStatusUpdate("checked_in")}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl transition-colors"
                    >
                      <Play size={16} />
                      Check In
                    </button>
                    <button
                      onClick={() => handleStatusUpdate("no_show")}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl transition-colors"
                    >
                      No Show
                    </button>
                    <button
                      onClick={() => handleStatusUpdate("cancelled")}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {appointment.status === "checked_in" && (
                  <button
                    onClick={() => handleStatusUpdate("completed")}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
                  >
                    <CheckCircle size={16} />
                    Mark as Completed
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </GlassCard>
      </motion.div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Add Appointment Modal Component
// ────────────────────────────────────────────────────────────────────────────

interface AddAppointmentModalProps {
  initialDate: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AddAppointmentModal({ initialDate, onClose, onSuccess }: AddAppointmentModalProps) {
  const toast = useToast();

  // Form state
  const [step, setStep] = useState<"client" | "details" | "datetime">("client");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [clients, setClients] = useState<Client[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Selections
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  // New client form
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [clientSearch, setClientSearch] = useState("");

  // Load initial data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [clientsData, locationsData, servicesData, providersData] = await Promise.all([
          getClients({ limit: 100 }),
          getLocations(true),
          getServices({ active: true }),
          getProviders(),
        ]);
        setClients(clientsData.clients);
        setLocations(locationsData);
        setServices(servicesData);
        setProviders(providersData.filter(p => p.isActive));

        // Auto-select if only one option
        if (locationsData.length === 1) {
          setSelectedLocation(locationsData[0] ?? null);
        }
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Filter providers by selected service
  const filteredProviders = useMemo(() => {
    if (!selectedService) return providers;
    return providers.filter(p =>
      p.services.some(s => s.id === selectedService.id)
    );
  }, [providers, selectedService]);

  // Load time slots when provider, service, location, and date are selected
  useEffect(() => {
    async function loadSlots() {
      if (!selectedProvider || !selectedService || !selectedLocation || !selectedDate) {
        setTimeSlots([]);
        return;
      }

      setSlotsLoading(true);
      try {
        const availability = await getProviderAvailability(
          selectedProvider.id,
          selectedService.id,
          selectedLocation.id,
          selectedDate
        );
        setTimeSlots(availability.slots || []);
      } catch (err) {
        console.error("Failed to load time slots:", err);
        setTimeSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    }
    loadSlots();
  }, [selectedProvider, selectedService, selectedLocation, selectedDate]);

  // Handle form submission
  async function handleSubmit() {
    // Validate required fields
    if (!selectedLocation || !selectedService || !selectedProvider || !selectedDate || !selectedTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      let clientId = selectedClient?.id;

      // Create new client if needed
      if (isNewClient) {
        if (!newClientName.trim()) {
          toast.error("Client name is required");
          setSubmitting(false);
          return;
        }
        const newClient = await createClient({
          name: newClientName.trim(),
          email: newClientEmail.trim() || undefined,
          phone: newClientPhone.trim() || undefined,
        });
        clientId = newClient.id;
      }

      if (!clientId) {
        toast.error("Please select or create a client");
        setSubmitting(false);
        return;
      }

      const appointmentData: CreateAppointmentData = {
        clientId,
        serviceId: selectedService.id,
        providerId: selectedProvider.id,
        locationId: selectedLocation.id,
        date: selectedDate,
        startTime: selectedTime,
        notes: notes.trim() || undefined,
      };

      await createAppointment(appointmentData);
      toast.success("Appointment created successfully!");
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to create appointment";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search)
    );
  }, [clients, clientSearch]);

  const formatTime = (time: string) => {
    const parts = time.split(":");
    const hours = parts[0] ?? "00";
    const minutes = parts[1] ?? "00";
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const canProceedToDetails = selectedClient || (isNewClient && newClientName.trim());
  const canProceedToDateTime = selectedLocation && selectedService && selectedProvider;
  const canSubmit = canProceedToDateTime && selectedDate && selectedTime;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <GlassCard className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-af-900 dark:text-af-100">
                Add Appointment
              </h2>
              <p className="text-sm text-af-500 mt-1">
                {step === "client" && "Select or create a client"}
                {step === "details" && "Choose service details"}
                {step === "datetime" && "Select date and time"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-af-100 dark:hover:bg-af-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-af-500" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-af-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Step Indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {["client", "details", "datetime"].map((s, i) => (
                  <div key={s} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === s
                        ? "bg-af-700 text-white"
                        : (["client", "details", "datetime"].indexOf(step) > i
                            ? "bg-green-500 text-white"
                            : "bg-af-200 dark:bg-af-700 text-af-500")
                    }`}>
                      {["client", "details", "datetime"].indexOf(step) > i ? "✓" : i + 1}
                    </div>
                    {i < 2 && (
                      <div className={`w-8 h-0.5 ${
                        ["client", "details", "datetime"].indexOf(step) > i
                          ? "bg-green-500"
                          : "bg-af-200 dark:bg-af-700"
                      }`} />
                    )}
                  </div>
                ))}
              </div>

              {/* Step 1: Client Selection */}
              {step === "client" && (
                <div className="space-y-4">
                  {/* Toggle between existing and new client */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsNewClient(false)}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        !isNewClient
                          ? "bg-af-700 text-white"
                          : "bg-af-100 dark:bg-af-800 text-af-700 dark:text-af-300"
                      }`}
                    >
                      Existing Client
                    </button>
                    <button
                      onClick={() => setIsNewClient(true)}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        isNewClient
                          ? "bg-af-700 text-white"
                          : "bg-af-100 dark:bg-af-800 text-af-700 dark:text-af-300"
                      }`}
                    >
                      New Client
                    </button>
                  </div>

                  {!isNewClient ? (
                    <>
                      {/* Client Search */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search clients..."
                          value={clientSearch}
                          onChange={(e) => setClientSearch(e.target.value)}
                          className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500"
                        />
                      </div>

                      {/* Client List */}
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {filteredClients.length === 0 ? (
                          <p className="text-center py-4 text-af-500 text-sm">
                            No clients found
                          </p>
                        ) : (
                          filteredClients.map((client) => (
                            <button
                              key={client.id}
                              onClick={() => setSelectedClient(client)}
                              className={`w-full p-3 rounded-lg text-left transition-colors ${
                                selectedClient?.id === client.id
                                  ? "bg-af-700 text-white"
                                  : "bg-white/30 dark:bg-af-800/30 hover:bg-white/50 dark:hover:bg-af-800/50"
                              }`}
                            >
                              <p className="font-medium">{client.name}</p>
                              {(client.email || client.phone) && (
                                <p className={`text-xs ${selectedClient?.id === client.id ? "text-white/70" : "text-af-500"}`}>
                                  {client.email || client.phone}
                                </p>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* New Client Form */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                            placeholder="Client name"
                            className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={newClientEmail}
                            onChange={(e) => setNewClientEmail(e.target.value)}
                            placeholder="client@email.com"
                            className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={newClientPhone}
                            onChange={(e) => setNewClientPhone(e.target.value)}
                            placeholder="(555) 555-5555"
                            className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <button
                    onClick={() => setStep("details")}
                    disabled={!canProceedToDetails}
                    className="w-full py-3 px-4 rounded-xl bg-af-700 hover:bg-af-800 disabled:bg-af-300 disabled:cursor-not-allowed text-white font-medium transition-colors"
                  >
                    Next: Service Details
                  </button>
                </div>
              )}

              {/* Step 2: Service Details */}
              {step === "details" && (
                <div className="space-y-4">
                  {/* Selected Client Summary */}
                  <div className="p-3 bg-af-100 dark:bg-af-800 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-af-500" />
                      <span className="text-sm font-medium text-af-900 dark:text-af-100">
                        {isNewClient ? newClientName : selectedClient?.name}
                      </span>
                    </div>
                    <button
                      onClick={() => setStep("client")}
                      className="text-xs text-af-500 hover:text-af-700"
                    >
                      Change
                    </button>
                  </div>

                  {/* Location Selection */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Location *
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {locations.map((location) => (
                        <button
                          key={location.id}
                          onClick={() => setSelectedLocation(location)}
                          className={`p-3 rounded-lg text-left transition-colors ${
                            selectedLocation?.id === location.id
                              ? "bg-af-700 text-white"
                              : "bg-white/30 dark:bg-af-800/30 hover:bg-white/50 dark:hover:bg-af-800/50"
                          }`}
                        >
                          <p className="font-medium">{location.name}</p>
                          {location.address && (
                            <p className={`text-xs ${selectedLocation?.id === location.id ? "text-white/70" : "text-af-500"}`}>
                              {location.address}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Service *
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {services.map((service) => (
                        <button
                          key={service.id}
                          onClick={() => {
                            setSelectedService(service);
                            // Reset provider if they don't offer this service
                            if (selectedProvider && !selectedProvider.services.some(s => s.id === service.id)) {
                              setSelectedProvider(null);
                            }
                          }}
                          className={`w-full p-3 rounded-lg text-left transition-colors ${
                            selectedService?.id === service.id
                              ? "bg-af-700 text-white"
                              : "bg-white/30 dark:bg-af-800/30 hover:bg-white/50 dark:hover:bg-af-800/50"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <p className="font-medium">{service.name}</p>
                            <p className={`text-sm ${selectedService?.id === service.id ? "text-white/70" : "text-af-500"}`}>
                              ${parseFloat(service.price).toFixed(0)}
                            </p>
                          </div>
                          <p className={`text-xs ${selectedService?.id === service.id ? "text-white/70" : "text-af-500"}`}>
                            {service.durationMinutes} min
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Provider Selection */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Provider *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {filteredProviders.map((provider) => (
                        <button
                          key={provider.id}
                          onClick={() => setSelectedProvider(provider)}
                          className={`p-3 rounded-lg text-left transition-colors ${
                            selectedProvider?.id === provider.id
                              ? "bg-af-700 text-white"
                              : "bg-white/30 dark:bg-af-800/30 hover:bg-white/50 dark:hover:bg-af-800/50"
                          }`}
                        >
                          <p className="font-medium text-sm">{provider.name}</p>
                          {provider.title && (
                            <p className={`text-xs ${selectedProvider?.id === provider.id ? "text-white/70" : "text-af-500"}`}>
                              {provider.title}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                    {filteredProviders.length === 0 && (
                      <p className="text-sm text-af-500 text-center py-2">
                        No providers available for this service
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setStep("client")}
                      className="flex-1 py-3 px-4 rounded-xl bg-af-200 dark:bg-af-700 text-af-700 dark:text-af-200 font-medium transition-colors hover:bg-af-300 dark:hover:bg-af-600"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setStep("datetime")}
                      disabled={!canProceedToDateTime}
                      className="flex-1 py-3 px-4 rounded-xl bg-af-700 hover:bg-af-800 disabled:bg-af-300 disabled:cursor-not-allowed text-white font-medium transition-colors"
                    >
                      Next: Date & Time
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Date & Time */}
              {step === "datetime" && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="p-3 bg-af-100 dark:bg-af-800 rounded-lg space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-af-500">Client</span>
                      <span className="text-sm font-medium text-af-900 dark:text-af-100">
                        {isNewClient ? newClientName : selectedClient?.name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-af-500">Service</span>
                      <span className="text-sm font-medium text-af-900 dark:text-af-100">
                        {selectedService?.name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-af-500">Provider</span>
                      <span className="text-sm font-medium text-af-900 dark:text-af-100">
                        {selectedProvider?.name}
                      </span>
                    </div>
                    <button
                      onClick={() => setStep("details")}
                      className="text-xs text-af-500 hover:text-af-700"
                    >
                      Change
                    </button>
                  </div>

                  {/* Date Selection */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setSelectedTime(null);
                      }}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500"
                    />
                  </div>

                  {/* Time Slots */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Time *
                    </label>
                    {slotsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin h-6 w-6 border-2 border-af-500 border-t-transparent rounded-full" />
                      </div>
                    ) : timeSlots.length === 0 ? (
                      <p className="text-sm text-af-500 text-center py-4">
                        No available time slots for this date
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                        {timeSlots.map((slot) => (
                          <button
                            key={slot.startTime}
                            onClick={() => setSelectedTime(slot.startTime)}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                              selectedTime === slot.startTime
                                ? "bg-af-700 text-white"
                                : "bg-white/30 dark:bg-af-800/30 hover:bg-white/50 dark:hover:bg-af-800/50 text-af-900 dark:text-af-100"
                            }`}
                          >
                            {formatTime(slot.startTime)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-af-700 dark:text-af-300 mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes for this appointment..."
                      rows={2}
                      className="w-full px-4 py-2 rounded-xl bg-white/50 dark:bg-af-800/50 border border-af-200 dark:border-af-700 focus:outline-none focus:ring-2 focus:ring-af-500 resize-none"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setStep("details")}
                      className="flex-1 py-3 px-4 rounded-xl bg-af-200 dark:bg-af-700 text-af-700 dark:text-af-200 font-medium transition-colors hover:bg-af-300 dark:hover:bg-af-600"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit || submitting}
                      className="flex-1 py-3 px-4 rounded-xl bg-green-600 hover:bg-green-700 disabled:bg-af-300 disabled:cursor-not-allowed text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={16} />
                          Create Appointment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </GlassCard>
      </motion.div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Month Calendar Component
// ────────────────────────────────────────────────────────────────────────────

interface MonthCalendarProps {
  month: string;
  days: Array<{ date: string; total: number; scheduled: number; completed: number; noShow: number }>;
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

function MonthCalendar({ month, days, selectedDate, onSelectDate }: MonthCalendarProps) {
  const parts = month.split("-").map(Number);
  const year = parts[0] ?? 2024;
  const monthNum = parts[1] ?? 1;

  // Build calendar grid
  const firstDay = new Date(year, monthNum - 1, 1).getDay();
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  const todayParts = new Date().toISOString().split("T");
  const today = todayParts[0] ?? new Date().toISOString().slice(0, 10);

  // Create lookup for day data
  const dayDataMap = new Map(days.map((d) => [d.date, d]));

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  // Fill in empty days before first day
  for (let i = 0; i < firstDay; i++) {
    currentWeek.push(null);
  }

  // Fill in days
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Fill in remaining empty days
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }

  return (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-af-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((day, idx) => {
          if (day === null) {
            return <div key={idx} className="aspect-square" />;
          }

          const dateStr = `${year}-${String(monthNum).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayData = dayDataMap.get(dateStr);
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === today;

          return (
            <button
              key={idx}
              onClick={() => onSelectDate(dateStr)}
              className={`
                aspect-square rounded-lg p-1 flex flex-col items-center justify-center
                transition-all duration-150 relative
                ${isSelected
                  ? "ring-2 ring-af-700 dark:ring-blue-400 bg-af-200 dark:bg-slate-700"
                  : "hover:bg-af-200/50 dark:hover:bg-slate-700/50"
                }
                ${isToday && !isSelected ? "ring-2 ring-blue-400 dark:ring-blue-500" : ""}
              `}
            >
              <span
                className={`text-sm font-medium ${isSelected ? "text-af-900 dark:text-white" : "text-af-800 dark:text-slate-200"}`}
              >
                {day}
              </span>
              {dayData && dayData.total > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {dayData.scheduled > 0 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  )}
                  {dayData.completed > 0 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  )}
                  {dayData.noShow > 0 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Appointment Card Component
// ────────────────────────────────────────────────────────────────────────────

interface AppointmentCardProps {
  appointment: Appointment;
  onStatusChange: (id: string, status: AppointmentStatus) => void;
  onClick: () => void;
}

function AppointmentCard({ appointment, onStatusChange, onClick }: AppointmentCardProps) {
  const [showEditMenu, setShowEditMenu] = useState(false);

  const statusColors: Record<AppointmentStatus, string> = {
    scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300",
    checked_in: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300",
    completed: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
    no_show: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
    cancelled: "bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300",
  };

  const formatTime = (time: string) => {
    const parts = time.split(":");
    const hours = parts[0] ?? "00";
    const minutes = parts[1] ?? "00";
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const handleStatusEdit = (newStatus: AppointmentStatus) => {
    onStatusChange(appointment.id, newStatus);
    setShowEditMenu(false);
  };

  // Check if appointment is in a terminal state (can be edited)
  const isTerminalState = ["completed", "no_show", "cancelled"].includes(appointment.status);

  return (
    <div
      onClick={onClick}
      className="p-3 rounded-lg bg-white/10 dark:bg-white/5 hover:bg-white/25 dark:hover:bg-white/15 hover:shadow-md cursor-pointer transition-all duration-150 border border-transparent hover:border-af-300 dark:hover:border-af-600"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-af-500" />
          <span className="text-sm font-medium text-af-900 dark:text-af-100">
            {formatTime(appointment.startTime)}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${statusColors[appointment.status]}`}
          >
            {appointment.status.replace("_", " ")}
          </span>
          {appointment.campaignLeadId && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-medium">
              Promo
            </span>
          )}
          {isTerminalState && (
            <div className="relative">
              <button
                onClick={() => setShowEditMenu(!showEditMenu)}
                title="Edit status (fix mistake)"
                className="p-1 text-af-400 hover:text-af-700 dark:hover:text-af-200 hover:bg-white/20 rounded transition-colors"
              >
                <Edit2 size={12} />
              </button>
              {showEditMenu && (
                <div className="absolute right-0 top-full mt-1 z-10 bg-white dark:bg-af-800 rounded-lg shadow-lg border border-af-200 dark:border-af-700 py-1 min-w-[140px]">
                  <p className="px-3 py-1 text-xs text-af-500 border-b border-af-200 dark:border-af-700">
                    Change status to:
                  </p>
                  <button
                    onClick={() => handleStatusEdit("scheduled")}
                    className="w-full px-3 py-1.5 text-left text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2"
                  >
                    <Calendar size={12} />
                    Scheduled
                  </button>
                  <button
                    onClick={() => handleStatusEdit("checked_in")}
                    className="w-full px-3 py-1.5 text-left text-xs text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 flex items-center gap-2"
                  >
                    <Play size={12} />
                    Checked In
                  </button>
                  <button
                    onClick={() => handleStatusEdit("completed")}
                    className="w-full px-3 py-1.5 text-left text-xs text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 flex items-center gap-2"
                  >
                    <CheckCircle size={12} />
                    Completed
                  </button>
                  <button
                    onClick={() => handleStatusEdit("no_show")}
                    className="w-full px-3 py-1.5 text-left text-xs text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                  >
                    <AlertCircle size={12} />
                    No Show
                  </button>
                  <button
                    onClick={() => handleStatusEdit("cancelled")}
                    className="w-full px-3 py-1.5 text-left text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900/30 flex items-center gap-2"
                  >
                    <XCircle size={12} />
                    Cancelled
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-2">
        <p className="text-sm font-medium text-af-900 dark:text-af-100">
          {appointment.service?.name ?? "Campaign Appointment"}
        </p>
        {appointment.campaignName && (
          <p className="text-xs text-purple-400 mt-0.5">{appointment.campaignName}</p>
        )}
        {appointment.vrConfirmationStatus && appointment.vrConfirmationStatus !== "unconfirmed" && (
          <div className="mt-1">
            <VrConfirmationChip
              status={appointment.vrConfirmationStatus}
              attempts={appointment.vrConfirmationAttempts}
            />
          </div>
        )}
        <div className="flex items-center gap-1 mt-1">
          <User size={12} className="text-af-500" />
          <span className="text-xs text-af-600 dark:text-af-400">
            {appointment.client.name}
          </span>
        </div>
        <p className="text-xs text-af-500 mt-1">
          {[appointment.provider?.name, appointment.location?.name].filter(Boolean).join(" @ ") || "—"}
        </p>
      </div>

      {/* Action buttons based on status */}
      {appointment.status === "scheduled" && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onStatusChange(appointment.id, "checked_in")}
            title="Check in client for their appointment"
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-700 dark:text-yellow-300 rounded transition-colors"
          >
            <Play size={12} />
            Check In
          </button>
          <button
            onClick={() => onStatusChange(appointment.id, "no_show")}
            title="Mark as No-Show (client did not arrive)"
            className="flex items-center justify-center gap-1 px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-700 dark:text-red-300 rounded transition-colors"
          >
            <AlertCircle size={12} />
          </button>
          <button
            onClick={() => onStatusChange(appointment.id, "cancelled")}
            title="Cancel this appointment"
            className="flex items-center justify-center gap-1 px-2 py-1 text-xs bg-gray-500/20 hover:bg-gray-500/30 text-gray-700 dark:text-gray-300 rounded transition-colors"
          >
            <XCircle size={12} />
          </button>
        </div>
      )}

      {appointment.status === "checked_in" && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onStatusChange(appointment.id, "completed")}
            title="Mark appointment as completed"
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-green-500/20 hover:bg-green-500/30 text-green-700 dark:text-green-300 rounded transition-colors"
          >
            <CheckCircle size={12} />
            Complete
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Stat Card Component
// ────────────────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string;
  subtext?: string;
  icon: React.ElementType;
  delay?: number;
}

function StatCard({ title, value, subtext, icon: Icon, delay = 0 }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <GlassCard className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-label text-af-500 mb-1">{title}</p>
            <p className="text-metric text-af-900 dark:text-af-100">{value}</p>
            {subtext && (
              <p className="text-xs text-af-500 mt-1">{subtext}</p>
            )}
          </div>
          <div className="p-2 rounded-lg bg-af-100/50 dark:bg-af-900/30">
            <Icon size={20} className="text-af-500" />
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Summary Item Component
// ────────────────────────────────────────────────────────────────────────────

interface SummaryItemProps {
  label: string;
  value: number | string;
  color?: "blue" | "green" | "red" | "gray";
  isText?: boolean;
}

function SummaryItem({ label, value, color, isText }: SummaryItemProps) {
  const colorClasses = {
    blue: "text-blue-600 dark:text-blue-400",
    green: "text-green-600 dark:text-green-400",
    red: "text-red-600 dark:text-red-400",
    gray: "text-gray-600 dark:text-gray-400",
  };

  return (
    <div className="text-center">
      <p className="text-xs text-af-500 mb-1">{label}</p>
      <p
        className={`text-lg font-semibold ${
          color ? colorClasses[color] : "text-af-900 dark:text-af-100"
        }`}
      >
        {isText ? value : value.toLocaleString()}
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ────────────────────────────────────────────────────────────────────────────

function formatDateHeader(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayParts = today.toISOString().split("T");
  const tomorrowParts = tomorrow.toISOString().split("T");
  const todayStr = todayParts[0] ?? today.toISOString().slice(0, 10);
  const tomorrowStr = tomorrowParts[0] ?? tomorrow.toISOString().slice(0, 10);

  if (dateStr === todayStr) {
    return "Today";
  } else if (dateStr === tomorrowStr) {
    return "Tomorrow";
  } else {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Exported Dashboard Page
// ────────────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
}
